/**
 * Estadísticas de vistas y aprobación de reportes.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList, PieChart, Pie } from 'recharts';
import { ink, studio, FilterDock, FilterPill, FilterDate, FilterMeta } from './studioUi';

const COLORS = ['#5E35B1', '#0288d1', '#137333', '#e65100', '#c5221f', '#c2185b', '#7b1fa2', '#00897b', '#f9a825', '#5f6368'];

const scoreColor = (score) => {
    if (score >= 95) return '#137333';
    if (score >= 80) return '#e65100';
    return ink.danger;
};

const StatisticsPanel = ({ clients, projects, biblioteca, areasList, fetchWithAuth, API_URL }) => {
    const [filters, setFilters] = useState({
        clientId: '',
        projectId: '',
        area: '',
        startDate: '',
        endDate: '',
    });
    const [stats, setStats] = useState({
        totalViews: 0,
        timeSeries: [],
        topReports: [],
        topUsers: [],
        viewsByClient: [],
        approvalPercentage: 0,
        approvalByClient: [],
        viewsOverTimeByReport: {},
        viewsByArea: [],
        approvalByArea: [],
    });
    const [loading, setLoading] = useState(false);
    const [modalChart, setModalChart] = useState(null);

    const filteredProjects = filters.clientId ? projects.filter((project) => project.clientId === filters.clientId) : projects;

    const findReportNameById = useCallback((reportId) => {
        if (!biblioteca) return 'Desconocido';
        const report = biblioteca.find((item) => item.id === reportId);
        return report ? report.reporteNombre : 'Desconocido';
    }, [biblioteca]);

    const fetchStatistics = useCallback(async () => {
        setLoading(true);
        const load = async (path) => {
            try {
                const res = await fetchWithAuth(`${API_URL}${path}`);
                return await res.json();
            } catch (error) {
                console.warn(`No se pudo cargar ${path}:`, error.message);
                return {};
            }
        };
        try {
            const queryParams = new URLSearchParams();
            if (filters.clientId) queryParams.append('clientId', filters.clientId);
            if (filters.projectId) queryParams.append('projectId', filters.projectId);
            if (filters.area) queryParams.append('area', filters.area);
            if (filters.startDate) queryParams.append('startDate', filters.startDate);
            if (filters.endDate) queryParams.append('endDate', filters.endDate);
            const query = queryParams.toString();
            const q = query ? `?${query}` : '';

            const [
                viewsOverTimeData,
                topReportsData,
                topUsersData,
                viewsByClientData,
                approvalPercentageData,
                approvalByClientData,
                viewsByReportData,
                viewsByAreaData,
                approvalByAreaData,
            ] = await Promise.all([
                load(`/api/reports/views-over-time${q}`),
                load(`/api/reports/top-reports${q}`),
                load(`/api/reports/top-users${q}`),
                load(`/api/reports/views-by-client${q}`),
                load(`/api/reports/approval-percentage${q}`),
                load(`/api/reports/approval-by-client${q}`),
                load(`/api/reports/views-over-time-by-report${q}`),
                load(`/api/reports/views-by-area${q}`),
                load(`/api/reports/approval-by-area${q}`),
            ]);

            setStats({
                totalViews: viewsOverTimeData.totalViews || 0,
                timeSeries: viewsOverTimeData.timeSeries || [],
                topReports: topReportsData.topReports || [],
                topUsers: topUsersData.topUsers || [],
                viewsByClient: viewsByClientData.viewsByClient || [],
                approvalPercentage: approvalPercentageData.approvalPercentage || 0,
                approvalByClient: approvalByClientData.approvalByClient || [],
                viewsOverTimeByReport: viewsByReportData.timeSeriesByReport || {},
                viewsByArea: viewsByAreaData.viewsByArea || [],
                approvalByArea: approvalByAreaData.approvalByArea || [],
            });
        } catch (error) {
            console.error('Error fetching statistics:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth, API_URL, filters]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    const resetFilters = () => {
        setFilters({ clientId: '', projectId: '', area: '', startDate: '', endDate: '' });
    };

    const truncateTick = (tick) => {
        if (tick && typeof tick === 'string' && tick.length > 22) {
            return `${tick.substring(0, 18)}…`;
        }
        return tick;
    };

    const filteredReports = useMemo(() => {
        return (biblioteca || []).filter((report) => {
            if (filters.clientId && report.clientId !== filters.clientId) return false;
            if (filters.projectId && report.projectId !== filters.projectId) return false;
            return true;
        });
    }, [biblioteca, filters.clientId, filters.projectId]);

    const catalogKpis = useMemo(() => {
        const uniqueClients = new Set(filteredReports.map((report) => report.clientId).filter(Boolean));
        const uniqueProjects = new Set(filteredReports.map((report) => report.projectId).filter(Boolean));
        let complete = 0;
        let completeLate = 0;
        let pending = 0;
        let overdue = 0;
        let withoutOwners = 0;
        filteredReports.forEach((report) => {
            if (!report.totalResponsibles) {
                withoutOwners += 1;
                return;
            }
            if (report.approvalStatus === 'complete_late') completeLate += 1;
            else if (report.approvalStatus === 'complete') complete += 1;
            else if (report.approvalStatus === 'overdue') overdue += 1;
            else pending += 1;
        });
        return {
            reportCount: filteredReports.length,
            clientCount: uniqueClients.size,
            projectCount: uniqueProjects.size,
            complete,
            completeLate,
            pending,
            overdue,
            withoutOwners,
        };
    }, [filteredReports]);

    const statusChart = useMemo(() => ([
        { name: 'Completos', value: catalogKpis.complete, fill: '#137333' },
        { name: 'Con retrasos', value: catalogKpis.completeLate, fill: '#c05621' },
        { name: 'Pendientes', value: catalogKpis.pending, fill: '#e65100' },
        { name: 'Vencidos', value: catalogKpis.overdue, fill: ink.danger },
        { name: 'Sin responsables', value: catalogKpis.withoutOwners, fill: ink.faint },
    ]).filter((row) => row.value > 0), [catalogKpis]);

    const worstArea = useMemo(() => {
        return (stats.approvalByArea || []).find((row) => row.total > 0) || stats.approvalByArea[0] || null;
    }, [stats.approvalByArea]);

    const quietestArea = useMemo(() => {
        const rows = [...(stats.viewsByArea || [])].sort((a, b) => a.count - b.count);
        return rows[0] || null;
    }, [stats.viewsByArea]);

    const viewsByReportChartData = useMemo(() => {
        const dataByDate = {};
        const reportNames = new Set();

        Object.entries(stats.viewsOverTimeByReport).forEach(([reportId, timeSeries]) => {
            const reportName = findReportNameById(reportId);
            reportNames.add(reportName);
            timeSeries.forEach((point) => {
                if (!dataByDate[point.date]) dataByDate[point.date] = { date: point.date };
                dataByDate[point.date][reportName] = point.count;
            });
        });

        return {
            chartData: Object.values(dataByDate).sort((a, b) => new Date(a.date) - new Date(b.date)),
            reportNames: Array.from(reportNames),
        };
    }, [stats.viewsOverTimeByReport, findReportNameById]);

    const areaChartHeight = Math.max(260, Math.min(Math.max(stats.viewsByArea.length, stats.approvalByArea.length), 12) * 36 + 48);

    const clientName = clients.find((client) => client.id === filters.clientId)?.nombre;
    const projectName = filteredProjects.find((project) => project.id === filters.projectId)?.nombre;

    return (
        <div style={studio.page}>
            <ChartModal
                chart={modalChart}
                onClose={() => setModalChart(null)}
                stats={stats}
                viewsByReportChartData={viewsByReportChartData}
                truncateTick={truncateTick}
            />
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Estadísticas</p>
                    <h2 style={studio.title}>Actividad de reportes</h2>
                    <p style={studio.subtitle}>Vistas, aprobación y responsabilidad por cliente, proyecto o área.</p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{stats.totalViews}</span>
                        <span style={studio.statLabel}>Vistas</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={{ ...studio.statValue, color: scoreColor(stats.approvalPercentage) }}>
                            {stats.approvalPercentage.toFixed(1)}%
                        </span>
                        <span style={studio.statLabel}>% aprob.</span>
                    </div>
                    {worstArea && (
                        <div style={{ ...studio.stat, borderLeftColor: ink.danger }}>
                            <span style={{ ...studio.statValue, fontSize: '1.05rem', color: ink.danger }}>{worstArea.area}</span>
                            <span style={studio.statLabel}>Peor área · {worstArea.approvalPercentage.toFixed(0)}%</span>
                        </div>
                    )}
                </div>
            </div>

            <FilterDock>
                <div style={studio.filterPills}>
                    <FilterPill
                        value={filters.clientId}
                        onChange={(value) => setFilters((prev) => ({ ...prev, clientId: value, projectId: '' }))}
                        emptyLabel="Cliente"
                    >
                        {clients.map((client) => <option key={client.id} value={client.id}>{client.nombre}</option>)}
                    </FilterPill>
                    <FilterPill
                        value={filters.projectId}
                        onChange={(value) => setFilters((prev) => ({ ...prev, projectId: value }))}
                        disabled={!filters.clientId}
                        emptyLabel="Proyecto"
                    >
                        {filteredProjects.map((project) => <option key={project.id} value={project.id}>{project.nombre}</option>)}
                    </FilterPill>
                    <FilterPill
                        value={filters.area}
                        onChange={(value) => setFilters((prev) => ({ ...prev, area: value }))}
                        emptyLabel="Área"
                    >
                        {(areasList || []).map((area) => <option key={area} value={area}>{area}</option>)}
                    </FilterPill>
                    <FilterDate value={filters.startDate} onChange={(value) => setFilters((prev) => ({ ...prev, startDate: value }))} title="Desde" />
                    <FilterDate value={filters.endDate} onChange={(value) => setFilters((prev) => ({ ...prev, endDate: value }))} title="Hasta" />
                </div>
                <FilterMeta
                    summary="Área recorta vistas (quién consultó) y aprobación (responsables de esa área). Las fechas aplican a vistas."
                    chips={[
                        filters.clientId && { key: 'c', label: clientName || filters.clientId, onClear: () => setFilters((prev) => ({ ...prev, clientId: '', projectId: '' })) },
                        filters.projectId && { key: 'p', label: projectName || filters.projectId, onClear: () => setFilters((prev) => ({ ...prev, projectId: '' })) },
                        filters.area && { key: 'a', label: filters.area, onClear: () => setFilters((prev) => ({ ...prev, area: '' })) },
                        filters.startDate && { key: 'from', label: `Desde ${filters.startDate}`, onClear: () => setFilters((prev) => ({ ...prev, startDate: '' })) },
                        filters.endDate && { key: 'to', label: `Hasta ${filters.endDate}`, onClear: () => setFilters((prev) => ({ ...prev, endDate: '' })) },
                    ].filter(Boolean)}
                    onClearAll={resetFilters}
                />
            </FilterDock>

            {loading ? (
                <div style={studio.empty}>Cargando estadísticas…</div>
            ) : (
                <>
                    <div style={ui.kpiGrid}>
                        <Kpi title="Clientes" value={catalogKpis.clientCount} />
                        <Kpi title="Proyectos" value={catalogKpis.projectCount} />
                        <Kpi title="Reportes" value={catalogKpis.reportCount} hint={`${catalogKpis.withoutOwners} sin responsables`} />
                        <Kpi title="Vistas" value={stats.totalViews} />
                        <Kpi
                            title="% de aprobación"
                            value={`${stats.approvalPercentage.toFixed(1)}%`}
                            color={scoreColor(stats.approvalPercentage)}
                            hint="Promedio de reportes con responsables"
                        />
                        <Kpi title="Pendientes" value={catalogKpis.pending} color={catalogKpis.pending ? ink.danger : undefined} hint={`${catalogKpis.overdue} vencidos · ${catalogKpis.completeLate} con retrasos`} />
                    </div>

                    <div style={ui.chartGrid}>
                        <ChartCard title="Vistas a lo largo del tiempo" hint="Serie temporal del consumo." onExpand={() => setModalChart({ id: 'viewsOverTime', title: 'Vistas a lo largo del tiempo' })} tall={300}>
                            <LineChart data={stats.timeSeries}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: ink.muted }} />
                                <YAxis tick={{ fontSize: 11, fill: ink.muted }} allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="count" name="Vistas" stroke="#5E35B1" strokeWidth={2} activeDot={{ r: 6 }}>
                                    <LabelList dataKey="count" position="top" style={{ fontSize: 10, fill: ink.muted }} />
                                </Line>
                            </LineChart>
                        </ChartCard>
                        <ChartCard title="Estado de aprobación" hint="Dona: completos, con retrasos, pendientes y vencidos del catálogo.">
                            <PieChart>
                                <Pie data={statusChart} dataKey="value" nameKey="name" innerRadius={58} outerRadius={88} paddingAngle={2}>
                                    {statusChart.map((row) => <Cell key={row.name} fill={row.fill} />)}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ChartCard>
                        <ChartCard title="Top 5 reportes más vistos" hint="Ranking de consumo." onExpand={() => setModalChart({ id: 'topReports', title: 'Top 5 reportes más vistos' })}>
                            <BarChart data={stats.topReports} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: ink.muted }} />
                                <YAxis type="category" dataKey="reportName" width={120} tick={{ fontSize: 11, fill: ink.muted }} tickFormatter={truncateTick} />
                                <Tooltip />
                                <Bar dataKey="count" name="Vistas" fill="#137333" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: ink.muted }} />
                            </BarChart>
                        </ChartCard>
                        <ChartCard title="Top 5 usuarios con más vistas" hint="Quién más consulta." onExpand={() => setModalChart({ id: 'topUsers', title: 'Top 5 usuarios con más vistas' })}>
                            <BarChart data={stats.topUsers} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: ink.muted }} />
                                <YAxis type="category" dataKey="userName" width={120} tick={{ fontSize: 11, fill: ink.muted }} tickFormatter={truncateTick} />
                                <Tooltip />
                                <Bar dataKey="count" name="Vistas" fill="#0288d1" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: ink.muted }} />
                            </BarChart>
                        </ChartCard>
                        <ChartCard title="Vistas por cliente" hint="Dónde se concentra el tráfico." onExpand={() => setModalChart({ id: 'viewsByClient', title: 'Vistas por cliente' })}>
                            <BarChart data={stats.viewsByClient} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: ink.muted }} />
                                <YAxis type="category" dataKey="clientName" width={120} tick={{ fontSize: 11, fill: ink.muted }} tickFormatter={truncateTick} />
                                <Tooltip />
                                <Bar dataKey="count" name="Vistas" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: ink.muted }}>
                                    {stats.viewsByClient.map((entry, index) => (
                                        <Cell key={entry.clientName} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartCard>
                        <ChartCard title="% de aprobación por cliente" hint="Responsabilidad por cuenta." onExpand={() => setModalChart({ id: 'approvalByClient', title: '% de aprobación por cliente' })}>
                            <BarChart data={stats.approvalByClient} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(tick) => `${tick}%`} tick={{ fontSize: 11, fill: ink.muted }} />
                                <YAxis type="category" dataKey="clientName" width={120} tick={{ fontSize: 11, fill: ink.muted }} tickFormatter={truncateTick} />
                                <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                                <Bar dataKey="approvalPercentage" name="% Aprobación" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: ink.muted, formatter: (value) => `${Number(value).toFixed(1)}%` }}>
                                    {stats.approvalByClient.map((entry) => (
                                        <Cell key={entry.clientId} fill={scoreColor(entry.approvalPercentage)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartCard>
                    </div>

                    <ChartCard
                        title="Vistas en el tiempo por reporte"
                        hint="Comparativo de consumo entre reportes del corte."
                        onExpand={() => setModalChart({ id: 'viewsByReport', title: 'Vistas en el tiempo por reporte' })}
                        tall={380}
                        wide
                    >
                        <LineChart data={viewsByReportChartData.chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: ink.muted }} />
                            <YAxis tick={{ fontSize: 11, fill: ink.muted }} allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            {viewsByReportChartData.reportNames.map((name, index) => (
                                <Line key={name} type="monotone" dataKey={name} stroke={COLORS[index % COLORS.length]} strokeWidth={2} dot={false} />
                            ))}
                        </LineChart>
                    </ChartCard>

                    <div style={ui.sectionHead}>
                        <div>
                            <p style={studio.kicker}>Por área</p>
                            <h3 style={{ ...studio.formTitle, margin: '4px 0 0' }}>Consumo y responsabilidad</h3>
                            <p style={{ ...studio.hint, marginTop: 6 }}>
                                Vistas según el área de quien consultó. Aprobación según el área del responsable. El filtro Área recorta ambos.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {worstArea && (
                                <div style={{ ...studio.stat, borderLeftColor: ink.danger, minWidth: 200 }}>
                                    <span style={studio.statLabel}>Peor aprobación</span>
                                    <span style={{ ...studio.statValue, fontSize: '1.15rem', color: ink.danger }}>{worstArea.area}</span>
                                    <span style={{ ...studio.hint, marginTop: 6 }}>
                                        {worstArea.approvalPercentage.toFixed(1)}% · {worstArea.pending} pendientes · {worstArea.reportCount} reportes
                                    </span>
                                </div>
                            )}
                            {quietestArea && (
                                <div style={{ ...studio.stat, borderLeftColor: '#0288d1', minWidth: 200 }}>
                                    <span style={studio.statLabel}>Menos vistas</span>
                                    <span style={{ ...studio.statValue, fontSize: '1.15rem', color: '#0288d1' }}>{quietestArea.area}</span>
                                    <span style={{ ...studio.hint, marginTop: 6 }}>{quietestArea.count} consultas</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={ui.chartGrid}>
                        <ChartCard title="Vistas por área" hint="Menos consumo arriba." tall={areaChartHeight}>
                            <BarChart data={stats.viewsByArea} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: ink.muted }} allowDecimals={false} />
                                <YAxis type="category" dataKey="area" width={110} tick={{ fontSize: 11, fill: ink.muted }} />
                                <Tooltip />
                                <Bar dataKey="count" name="Vistas" fill="#0288d1" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: ink.muted }} />
                            </BarChart>
                        </ChartCard>
                        <ChartCard title="% de aprobación por área" hint="Peor índice arriba. Color según el %." tall={areaChartHeight}>
                            <BarChart data={stats.approvalByArea} layout="vertical" margin={{ top: 5, right: 36, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(tick) => `${tick}%`} tick={{ fontSize: 11, fill: ink.muted }} />
                                <YAxis type="category" dataKey="area" width={110} tick={{ fontSize: 11, fill: ink.muted }} />
                                <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                                <Bar dataKey="approvalPercentage" name="% Aprobación" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: ink.muted, formatter: (value) => `${Number(value).toFixed(1)}%` }}>
                                    {stats.approvalByArea.map((entry) => (
                                        <Cell key={entry.area} fill={scoreColor(entry.approvalPercentage)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartCard>
                        <ChartCard title="Pendientes vs aprobados por área" hint="Barras agrupadas de responsabilidad." tall={areaChartHeight}>
                            <BarChart data={stats.approvalByArea} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: ink.muted }} allowDecimals={false} />
                                <YAxis type="category" dataKey="area" width={110} tick={{ fontSize: 11, fill: ink.muted }} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="approved" name="Aprobados" fill="#137333" radius={[0, 6, 6, 0]} />
                                <Bar dataKey="pending" name="Pendientes" fill="#e65100" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ChartCard>
                        <ChartCard title="Participación de vistas por área" hint="Dona: qué área concentra el consumo.">
                            <PieChart>
                                <Pie data={stats.viewsByArea} dataKey="count" nameKey="area" innerRadius={58} outerRadius={88} paddingAngle={2}>
                                    {stats.viewsByArea.map((entry, index) => (
                                        <Cell key={entry.area} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ChartCard>
                    </div>
                </>
            )}
        </div>
    );
};

function Kpi({ title, value, hint, color }) {
    return (
        <div style={studio.stat}>
            <span style={studio.statLabel}>{title}</span>
            <span style={{ ...studio.statValue, color: color || ink.text }}>{value}</span>
            {hint && <span style={{ ...studio.hint, marginTop: 6 }}>{hint}</span>}
        </div>
    );
}

function ChartCard({ title, hint, children, onExpand, tall, wide }) {
    return (
        <div style={{ ...studio.card, ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
            <div style={ui.chartHead}>
                <div>
                    <h4 style={ui.chartTitle}>{title}</h4>
                    {hint && <p style={{ ...studio.hint, marginTop: 4 }}>{hint}</p>}
                </div>
                {onExpand && (
                    <button type="button" onClick={onExpand} style={studio.ghostBtn} title="Ampliar gráfico">Ampliar</button>
                )}
            </div>
            <div style={{ height: tall || 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                    {children}
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const ChartModal = ({ chart, onClose, stats, viewsByReportChartData, truncateTick }) => {
    if (!chart) return null;

    const renderContent = () => {
        switch (chart.id) {
            case 'viewsOverTime':
                return (
                    <LineChart data={stats.timeSeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" name="Vistas" stroke="#5E35B1" strokeWidth={2}>
                            <LabelList dataKey="count" position="top" />
                        </Line>
                    </LineChart>
                );
            case 'topReports':
                return (
                    <BarChart data={stats.topReports} layout="vertical" margin={{ top: 20, right: 40, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="reportName" width={170} tickFormatter={truncateTick} />
                        <Tooltip />
                        <Bar dataKey="count" name="Vistas" fill="#137333" label={{ position: 'right' }} />
                    </BarChart>
                );
            case 'topUsers':
                return (
                    <BarChart data={stats.topUsers} layout="vertical" margin={{ top: 20, right: 40, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="userName" width={130} tickFormatter={truncateTick} />
                        <Tooltip />
                        <Bar dataKey="count" name="Vistas" fill="#0288d1" label={{ position: 'right' }} />
                    </BarChart>
                );
            case 'viewsByClient':
                return (
                    <BarChart data={stats.viewsByClient} layout="vertical" margin={{ top: 20, right: 40, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="clientName" width={130} tickFormatter={truncateTick} />
                        <Tooltip />
                        <Bar dataKey="count" name="Vistas" label={{ position: 'right' }}>
                            {stats.viewsByClient.map((entry, index) => (
                                <Cell key={entry.clientName} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                );
            case 'approvalByClient':
                return (
                    <BarChart data={stats.approvalByClient} layout="vertical" margin={{ top: 20, right: 40, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(tick) => `${tick}%`} />
                        <YAxis type="category" dataKey="clientName" width={130} tickFormatter={truncateTick} />
                        <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                        <Bar dataKey="approvalPercentage" name="% Aprobación" label={{ position: 'right', formatter: (value) => `${Number(value).toFixed(1)}%` }}>
                            {stats.approvalByClient.map((entry) => (
                                <Cell key={entry.clientId} fill={scoreColor(entry.approvalPercentage)} />
                            ))}
                        </Bar>
                    </BarChart>
                );
            case 'viewsByReport':
                return (
                    <LineChart data={viewsByReportChartData.chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={ink.line} />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {viewsByReportChartData.reportNames.map((name, index) => (
                            <Line key={name} type="monotone" dataKey={name} stroke={COLORS[index % COLORS.length]} />
                        ))}
                    </LineChart>
                );
            default:
                return <p style={studio.hint}>Gráfico no encontrado.</p>;
        }
    };

    return (
        <div style={ui.overlay} onClick={onClose} role="presentation">
            <div style={ui.sheet} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={chart.title}>
                <div style={ui.modalHead}>
                    <div>
                        <p style={studio.kicker}>Detalle</p>
                        <h3 style={{ ...studio.formTitle, margin: '6px 0 0' }}>{chart.title}</h3>
                    </div>
                    <button type="button" onClick={onClose} style={studio.ghostBtn}>Cerrar</button>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {renderContent()}
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

const ui = {
    kpiGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 10,
    },
    chartGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        gap: 12,
    },
    chartHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 12,
    },
    chartTitle: {
        margin: 0,
        fontSize: '1.05rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.02em',
    },
    sectionHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 16,
        flexWrap: 'wrap',
        marginTop: 8,
    },
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.45)',
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    sheet: {
        ...studio.card,
        width: 'min(1100px, 100%)',
        height: '82vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(28, 29, 33, 0.22)',
    },
    modalHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 16,
    },
};

export default StatisticsPanel;
